// @flow
/* eslint-disable no-param-reassign */
import {throttle} from "lodash";

type Callback = () => void;

function hasSmoothScroll() {
  return (
    document.documentElement &&
    "scrollBehavior" in document.documentElement.style
  );
}

function hasScrollElement() {
  return document.documentElement && "scrollTo" in document.documentElement;
}

// we use a 3px margin of tolerance by default to invoke the callback when it's "close enough"
function inProximity(number, targetNumber, tolerance = 3) {
  return (
    number === targetNumber ||
    (number < targetNumber && targetNumber - number < tolerance) ||
    (number > targetNumber && number - targetNumber < tolerance)
  );
}

function browserIsIE11orEdge() {
  return (
    window.navigator.userAgent.indexOf("Edge") > -1 ||
    window.navigator.userAgent.indexOf("Trident") > -1
  );
}

// scrollWindow calls the native scrollTo method and invokes a callback
// once the window has scrolled into position
export function scrollWindow(y: number, cb?: Callback = () => {}): void {
  if (inProximity(window.pageYOffset, y, 1)) {
    cb();
    return;
  }

  if (browserIsIE11orEdge()) {
    window.scrollTo(0, y);
    cb();
    return;
  }

  const scrollHandler = throttle(() => {
    if (inProximity(window.pageYOffset, y)) {
      cb();
      window.removeEventListener("scroll", scrollHandler);
    }
  }, 16);

  window.addEventListener("scroll", scrollHandler);
  window.scrollTo({top: y, behavior: "smooth"});
}

export function scrollToElement(element: HTMLElement, cb?: Callback) {
  if (!element) return;
  const yPosition = element.getBoundingClientRect().top + window.pageYOffset;
  scrollWindow(yPosition, cb);
}

type ScrollOptions = {
  top: number,
  left: number,
  behavior: string,
  block: string,
};

export function scrollElement(element: HTMLElement, options: ScrollOptions) {
  if (!element) return;

  const {top, left} = options;
  if (hasScrollElement() && hasSmoothScroll()) {
    // $FlowFixMe
    element.scrollTo({behavior: "smooth", ...options});
  } else if (hasScrollElement()) {
    // $FlowFixMe
    element.scrollTo(top, left);
  } else {
    if (top) element.scrollTop = top;
    if (left) element.scrollLeft = left;
  }
}
